// Centralized color system for LeadForge
// This file contains all the main colors used across the application
// Change these values to update the entire website's color scheme

export const colors = {
  // Primary brand colors
  primary: {
    main: '#1B4672',      // Light mode blue
    light: '#2A5A8A',     // Lighter blue for hover states
    dark: '#0F3A5A',      // Darker blue for pressed states
    contrast: '#FFFFFF',  // Text color on primary background
  },

  // Secondary colors
  secondary: {
    main: '#6052A2',      // Purple accent
    light: '#7A6BB8',     // Lighter purple
    dark: '#4A3D7A',      // Darker purple
    contrast: '#FFFFFF',  // Text color on secondary background
  },

  // Text colors
  text: {
    primary: '#202224',           // Main text color (dark)
    secondary: '#666666',         // Secondary text color
    disabled: '#999999',          // Disabled text color
    inverse: '#FFFFFF',           // Text on dark backgrounds
    muted: 'rgba(32, 34, 36, 0.6)', // Muted text
  },

  // Background colors
  background: {
    default: '#F5F6FA',           // Default page background
    paper: '#F5F6FA',             // Card/paper background
    elevated: '#F5F6FA',          // Elevated surfaces
    overlay: 'rgba(0, 0, 0, 0.5)', // Modal overlays
    // Page-specific backgrounds
    admin: '#F5F6FA',             // Admin dashboard background
    client: '#F5F6FA',            // Client dashboard background
    auth: '#F5F6FA',              // Authentication pages background
    sidebar: '#F5F6FA',           // Sidebar background
    header: '#F5F6FA',            // Header background
    card: '#F5F6FA',              // Card background
    modal: '#F5F6FA',             // Modal background
    table: '#F5F6FA',             // Table background
    form: '#F5F6FA',              // Form background
  },

  // Border colors
  border: {
    default: '#E2E8F0',           // Default borders
    light: '#F1F4F9',             // Light borders
    medium: '#D8D8D8',            // Medium borders - also for inputs
    dark: '#CBD5E1',              // Dark borders
    auth: '#B9B9B9',              // Auth card borders (Figma design)
  },

  // Status colors
  status: {
    success: '#2C7130',           // Success green
    warning: '#C46300',           // Warning orange
    error: '#A92626',             // Error red
    info: '#11509A',              // Info blue
  },

  // Dashboard specific colors
  dashboard: {
    firstRowBg: '#E8E2F3',        // First row background
    secondRowBg: '#F0E6F7',       // Second row background
  },

  // Gradient backgrounds
  gradients: {
    authDark: '#081028',  // Dark mode auth background
    authLight: 'linear-gradient(to bottom, #f8fcfd, #e6eaf1)', // Light mode auth background gradient
  },
} as const;

// Dark mode color overrides - Based on Figma design
export const darkColors = {
  // Primary brand colors (dark mode)
  primary: {
    main: '#1B4672',      // Brand color (same as light mode for consistency)
    light: '#2A5A8A',     // Lighter blue for hover states
    dark: '#0F3A5A',      // Darker blue for pressed states
    contrast: '#FFFFFF',  // Text color on primary background
  },

  // Secondary colors (same for consistency)
  secondary: {
    main: '#6052A2',
    light: '#7A6BB8',
    dark: '#4A3D7A',
    contrast: '#FFFFFF',
  },

  // Text colors (inverted for dark mode)
  text: {
    primary: '#FFFFFF',           // Main text color (light)
    secondary: 'rgba(255, 255, 255, 0.7)', // Secondary text color
    disabled: 'rgba(255, 255, 255, 0.5)',  // Disabled text color
    inverse: '#202224',           // Text on light backgrounds
    muted: 'rgba(255, 255, 255, 0.6)', // Muted text
  },

  // Background colors (dark theme) - Updated to match Figma
  background: {
    default: '#191919',           // Default page background (MAIN DARK1 from Figma)
    paper: '#2e2e30',             // Card/paper background (MAIN DARK 2 from Figma)
    elevated: '#2e2e30',          // Elevated surfaces
    overlay: 'rgba(0, 0, 0, 0.7)', // Modal overlays
    // Page-specific backgrounds
    admin: '#191919',             // Admin dashboard background
    client: '#191919',            // Client dashboard background
    auth: '#0e0e0e',              // Authentication pages background (DARK from Figma)
    sidebar: '#0e0e0e',           // Sidebar background (DARK from Figma - darker than main)
    header: '#2e2e30',            // Header background
    card: '#2e2e30',              // Card background (MAIN DARK 2 from Figma)
    modal: '#2e2e30',             // Modal background
    table: '#2e2e30',             // Table background
    form: '#2e2e30',              // Form background
  },

  // Border colors (dark theme)
  border: {
    default: 'rgba(255, 255, 255, 0.1)', // Default borders - more subtle
    light: 'rgba(255, 255, 255, 0.05)',   // Light borders
    medium: 'rgba(255, 255, 255, 0.15)',  // Medium borders
    dark: 'rgba(255, 255, 255, 0.2)',    // Dark borders
  },

  // Status colors (same for consistency)
  status: {
    success: '#2C7130',
    warning: '#C46300',
    error: '#A92626',
    info: '#11509A',
  },

  // Dashboard specific colors (dark theme)
  dashboard: {
    firstRowBg: '#2e2e30',        // First row background (using MAIN DARK 2)
    secondRowBg: '#2e2e30',       // Second row background (using MAIN DARK 2)
  },

  // Gradient backgrounds (dark theme)
  gradients: {
    authDark: '#0e0e0e',  // Dark mode auth background (DARK from Figma)
    authLight: 'linear-gradient(to bottom, #f8fcfd, #e6eaf1)', // Light mode auth background gradient
  },
} as const;

// Typography configuration - Using only Nunito Sans for consistency
export const typography = {
  fontFamily: '"Nunito Sans", sans-serif',
  fontWeights: {
    light: 300,
    regular: 400,
    medium: 500,
    semiBold: 600,
    bold: 700,
  },
  fontSizes: {
    xs: '0.75rem',    // 12px
    sm: '0.875rem',   // 14px
    base: '1rem',     // 16px
    lg: '1.125rem',   // 18px
    xl: '1.25rem',    // 20px
    '2xl': '1.5rem',  // 24px
    '3xl': '1.875rem', // 30px
    '4xl': '2.25rem', // 36px
  },
} as const;

// Spacing system
export const spacing = {
  xs: '4px',
  sm: '8px',
  md: '16px',
  lg: '24px',
  xl: '32px',
  '2xl': '48px',
  '3xl': '64px',
} as const;

// Border radius system
export const borderRadius = {
  sm: '4px',
  md: '8px',
  lg: '12px',
  xl: '16px',
  full: '50%',
} as const;

// Shadow system
export const shadows = {
  sm: '0 1px 2px rgba(0, 0, 0, 0.05)',
  md: '0 4px 6px rgba(0, 0, 0, 0.1)',
  lg: '0 10px 15px rgba(0, 0, 0, 0.1)',
  xl: '0 20px 25px rgba(0, 0, 0, 0.1)',
  dark: {
    sm: '0 1px 2px rgba(0, 0, 0, 0.2)',
    md: '0 4px 6px rgba(0, 0, 0, 0.3)',
    lg: '0 10px 15px rgba(0, 0, 0, 0.3)',
    xl: '0 20px 25px rgba(0, 0, 0, 0.3)',
  },
} as const;
